import { useEffect, useState } from "react";
import { MODAL_EXIT_DURATION_MS } from "@/components/ds/modal-tokens";

export interface ModalPresence {
  /** Whether the modal component should be rendered at all. */
  mounted: boolean;
  /** The `opened` value to pass to the modal — lags `mounted` by one frame on open. */
  opened: boolean;
}

/**
 * Drives the open/close microanimation for a modal the caller wants to *conditionally* render
 * (`{presence.mounted && <SomeModal opened={presence.opened} />}`), instead of keeping it mounted
 * for the lifetime of the page.
 *
 * Two Mantine facts make the naive `{isOpen && <Modal opened />}` skip the animation entirely:
 * - The **enter** transition only plays if Mantine first sees the modal *closed*, then open. A
 *   modal mounted already-open paints straight to the final state — no animation. So on open we
 *   mount closed (`opened=false`) and flip `opened=true` on the next animation frame, guaranteeing
 *   a painted closed frame for Mantine to animate away from.
 * - The **exit** transition only plays while the modal stays mounted. So on close we flip
 *   `opened=false` immediately (starting the exit) but defer unmount by one exit duration.
 *
 * Mounting only on open keeps a modal's on-mount fetches / dynamic imports off the page-load path,
 * and the fresh mount per open resets form/fetch state naturally.
 *
 * @param open - the caller's open flag (e.g. from `useDisclosure`)
 *
 * @example
 * const modal = useDisclosure();
 * const presence = useModalPresence(modal.opened);
 * return presence.mounted && (
 *   <SendFaxModal opened={presence.opened} onClose={modal.close} />
 * );
 */
export function useModalPresence(open: boolean): ModalPresence {
  const [mounted, setMounted] = useState(open);
  // Starts closed even when `open` is already true on first render, so the modal paints closed
  // before animating in (the enter transition needs that closed frame).
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Double rAF: let the modal paint fully closed (one frame to commit, one to paint) before
      // flipping open, so Mantine's Transition animates from the closed state.
      let innerFrame: number | undefined;
      const outerFrame = requestAnimationFrame(() => {
        innerFrame = requestAnimationFrame(() => setOpened(true));
      });
      return () => {
        cancelAnimationFrame(outerFrame);
        if (innerFrame !== undefined) cancelAnimationFrame(innerFrame);
      };
    }
    // Close: start the exit now, unmount once the transition has had time to play.
    setOpened(false);
    const timeout = setTimeout(() => setMounted(false), MODAL_EXIT_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  return { mounted, opened };
}
